import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Button,
  type Column,
  EmptyState,
  Icon,
  Pill,
  SearchInput,
  Select,
  Table,
} from "@/components/common";
import { Guard } from "@/auth/Guard";
import { useClients } from "@/hooks/useClients";
import { useOrganizations } from "@/hooks/useOrganizations";
import { getInitials } from "@/utils/initials";
import { formatDate } from "@/utils/fmt";
import type { Client } from "@/types/client";
import { ClientCreateModal } from "./ClientCreateModal";
import "./ClientsList.css";

type View = "table" | "grid";
type StatusFilter = "all" | "active" | "inactive";

type Props = {
  workspaceId: number;
  /** When set, scopes the list to one organization (hides the org column/filter). */
  organizationId?: number;
  /** External status filter (driven by the page sub-tabs). */
  status?: StatusFilter;
  /** Hide the toolbar create button (the page header provides its own). */
  hideCreate?: boolean;
};

export function ClientsList({
  workspaceId,
  organizationId,
  status = "all",
  hideCreate = false,
}: Props) {
  const navigate = useNavigate();
  const { data: clients = [], isLoading } = useClients(workspaceId, organizationId);
  const { data: orgs = [] } = useOrganizations(organizationId == null ? workspaceId : null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState<View>("table");
  const [orgFilter, setOrgFilter] = useState<number | "">("");
  const [creating, setCreating] = useState(false);

  const orgById = useMemo(() => {
    const m = new Map<number, string>();
    for (const o of orgs) m.set(o.id, o.nickname ?? o.legal_name);
    return m;
  }, [orgs]);

  const filtered = useMemo(() => {
    let rows = clients;
    if (status === "active") rows = rows.filter((c) => c.active);
    else if (status === "inactive") rows = rows.filter((c) => !c.active);
    if (orgFilter !== "") rows = rows.filter((c) => c.organization_id === orgFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (c) =>
          c.legal_name.toLowerCase().includes(q) ||
          c.document_masked.includes(q) ||
          (c.responsible_name ?? "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [clients, status, orgFilter, search]);

  const scoped = organizationId != null;

  const columns: Column<Client>[] = [
    {
      id: "empresa",
      header: "Razão social",
      cell: (e) => (
        <div className="emp-table-name">
          <span className="mark">{getInitials(e.legal_name)}</span>
          <div>
            <div className="razao">{e.legal_name}</div>
            <div className="fantasia">{e.document_masked}</div>
          </div>
        </div>
      ),
    },
    {
      id: "tipo",
      header: "Tipo",
      cell: (e) => (
        <Pill tone={e.document_type === "cnpj" ? "info" : "muted"}>
          {e.document_type.toUpperCase()}
        </Pill>
      ),
    },
    ...(scoped
      ? []
      : [
          {
            id: "org",
            header: "Organização",
            cell: (e: Client) => {
              const name = orgById.get(e.organization_id);
              return name ? (
                <span className="org-chip">
                  <span className="org-chip-mark">{getInitials(name)}</span>
                  <span className="org-chip-name">{name}</span>
                </span>
              ) : (
                <span className="td-muted">—</span>
              );
            },
          } as Column<Client>,
        ]),
    {
      id: "responsavel",
      header: "Responsável",
      cell: (e) => <span className="responsavel-name">{e.responsible_name ?? "—"}</span>,
    },
    {
      id: "status",
      header: "Status",
      cell: (e) => (
        <Pill tone={e.active ? "ativa" : "arq"}>{e.active ? "Ativa" : "Inativa"}</Pill>
      ),
    },
    ...(scoped
      ? []
      : [
          {
            id: "created",
            header: "Criada em",
            cell: (e: Client) => <span className="td-faded">{formatDate(e.created_at)}</span>,
          } as Column<Client>,
        ]),
  ];

  return (
    <>
      <div className="emp-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nome, CNPJ…" />
        {!scoped && (
          <Select
            variant="chip"
            label="Organização"
            value={orgFilter === "" ? null : String(orgFilter)}
            onChange={(v) => setOrgFilter(v === "" ? "" : Number(v))}
            options={orgs.map((o) => ({
              value: String(o.id),
              label: o.nickname ?? o.legal_name,
            }))}
            placeholder="Todas"
            clearable
            clearLabel="Todas as organizações"
          />
        )}
        <span className="emp-toolbar-counter">
          {filtered.length} de {clients.length}
        </span>
        <div className="emp-toolbar-spacer" />
        <div className="view-toggle">
          <button
            className={view === "table" ? "active" : ""}
            onClick={() => setView("table")}
            aria-label="Tabela"
          >
            <Icon name="list" size={15} />
          </button>
          <button
            className={view === "grid" ? "active" : ""}
            onClick={() => setView("grid")}
            aria-label="Grade"
          >
            <Icon name="grid" size={15} />
          </button>
        </div>
        {!hideCreate && (
          <Guard permission="clients.write">
            <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
              Nova empresa
            </Button>
          </Guard>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="briefcase"
          size="lg"
          title={isLoading ? "Carregando…" : "Nenhuma empresa"}
          description={isLoading ? undefined : "Cadastre a primeira empresa cliente."}
        />
      ) : view === "table" ? (
        <Table
          rows={filtered}
          columns={columns}
          getKey={(c) => String(c.id)}
          onRowClick={(c) => navigate(`/empresas/${c.id}`)}
          rowAction={() => (
            <span className="row-act" aria-hidden>
              <Icon name="more" size={16} />
            </span>
          )}
        />
      ) : (
        <div className="emp-grid">
          {filtered.map((e) => (
            <div key={e.id} className="emp-card" onClick={() => navigate(`/empresas/${e.id}`)}>
              <div className="emp-card-head">
                <span className="emp-card-mark">{getInitials(e.legal_name)}</span>
                <div className="emp-card-info">
                  <div className="emp-card-razao">{e.legal_name}</div>
                  <div className="emp-card-fantasia">{e.document_type === "cnpj" ? "PJ" : "PF"}</div>
                  <div className="emp-card-cnpj">{e.document_masked}</div>
                </div>
              </div>
              <div className="mb-14">
                <Pill tone={e.active ? "ativa" : "arq"}>{e.active ? "Ativa" : "Inativa"}</Pill>
              </div>
              <div className="emp-card-foot">
                <span className="responsavel-name">{e.responsible_name ?? "—"}</span>
                {!scoped && (
                  <span className="emp-card-uf">{orgById.get(e.organization_id) ?? ""}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <ClientCreateModal
          workspaceId={workspaceId}
          organizationId={organizationId}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}
